import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Sparkles } from "lucide-react";
import { Seo, breadcrumbLd, PROVIDER } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { SectionHead } from "@/components/site/SectionHead";
import { CaseCard, KpiTile } from "@/components/site/CaseCard";
import { CtaBand } from "@/components/site/CtaBand";
import { useContent, useItem, SITE_URL } from "@/lib/api";
import { StoryMetrics, StoryChallenge, StoryApproach, StoryServices, StoryOutcome, StoryTakeaway, StoryCta } from "@/components/site/CaseStory";
import { CampaignMetrics, CampaignIntro, CampaignSection, CampaignStrategy, CampaignWhy } from "@/components/site/CaseCampaigns";
import { SeoChallenge, SeoStrategy, SeoResult, SeoEnquiries, SeoOutcome } from "@/components/site/CaseSeo";
import NotFound from "./NotFound";

const list = (v) => (Array.isArray(v) ? v : []);
const GEO_TEXT = "Generative Engine Optimisation (GEO) is the practice of structuring a website's content so it can be clearly understood, trusted and referenced by AI-powered search experiences, such as Google AI Overviews, ChatGPT and Perplexity, alongside traditional search engines. It builds on strong SEO foundations: clear page structure, factual, well-organised content, descriptive context about the business and its services, and valid structured data. No one can guarantee inclusion in AI-generated answers, but GEO gives a website the best possible foundation to be discovered.";

const Background = ({ c }) => (
  <section className="bg-white py-20 sm:py-28" data-testid="cs-background">
    <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-[1fr_1.3fr]">
      <SectionHead eyebrow="Project background" title={c.campaign ? `The ${c.campaign} project` : "The project"} />
      <Reveal className="space-y-4 text-base leading-relaxed text-slate-600 md:text-lg">
        {list(c.background).map((p) => <p key={p}>{p}</p>)}
      </Reveal>
    </div>
  </section>
);

const Services = ({ c }) => (
  <section className="bg-[#F5F0FF] py-20 sm:py-28" data-testid="cs-services">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead eyebrow="What we did" title="Services delivered" />
      <div className={`mt-10 grid gap-4 ${list(c.services).length > 1 ? "md:grid-cols-2 lg:grid-cols-3" : ""}`}>
        {list(c.services).map((s, i) => (
          <Reveal key={s.title} delay={(i % 3) * 0.06} className="h-full rounded-[24px] bg-white p-7">
            <h3 className="text-lg font-extrabold text-[#13052A]">{list(c.services).length > 1 ? `${i + 1}. ` : ""}{s.title}</h3>
            <ul className={`mt-4 grid gap-2.5 ${list(c.services).length === 1 ? "sm:grid-cols-2" : ""}`}>
              {list(s.points).map((p) => <li key={p} className="flex items-start gap-2.5 text-sm text-slate-700"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#7C3AED]" />{p}</li>)}
            </ul>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ResultsBlock = ({ c }) => (
  <section className="grain relative overflow-hidden bg-[#13052A] py-20 text-white sm:py-28" data-testid="cs-results">
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead dark eyebrow="Verified results" title={c.highlight} />
      {list(c.metrics).length > 0 && (
        <div className="mt-10 grid gap-3 sm:grid-cols-3">{list(c.metrics).map((m) => <KpiTile key={m.label} m={m} dark />)}</div>
      )}
      <ul className="mt-8 grid gap-3 sm:grid-cols-2">
        {list(c.key_results).map((r) => <li key={r} className="flex items-start gap-3 text-base text-violet-50"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#60A5FA]" />{r}</li>)}
      </ul>
      {c.results_note && <p className="mt-6 text-sm text-violet-200">{c.results_note}</p>}
    </div>
  </section>
);

const Geo = () => (
  <section className="bg-white pt-20 sm:pt-28" data-testid="cs-geo">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="rounded-[28px] border border-violet-200 p-8 sm:p-10">
        <Sparkles className="h-6 w-6 text-[#7C3AED]" />
        <h2 className="mt-4 text-2xl font-extrabold text-[#13052A] sm:text-3xl">What is Generative Engine Optimisation (GEO)?</h2>
        <p className="mt-4 max-w-4xl text-base leading-relaxed text-slate-600">{GEO_TEXT}</p>
        <Link to="/seo" className="btn-ghost mt-6" data-testid="cs-geo-link">Explore SEO, GEO & AI Search <ArrowUpRight className="h-4 w-4" /></Link>
      </Reveal>
    </div>
  </section>
);

const Links = ({ c }) => (
  <section className="bg-white py-20 sm:py-24" data-testid="cs-links">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <h2 className="text-2xl font-extrabold text-[#13052A]">Related services</h2>
      <div className="mt-6 flex flex-wrap gap-3">
        {list(c.links).map(([to, label]) => <Link key={to} to={to} className="btn-ghost" data-testid={`cs-link${to.replace(/\//g, "-")}`}>{label} <ArrowUpRight className="h-4 w-4" /></Link>)}
      </div>
    </div>
  </section>
);

const More = ({ slug, related }) => {
  const { items } = useContent("case_studies");
  const others = Array.isArray(related) && related.length
    ? related.map((s) => items.find((x) => x.slug === s)).filter(Boolean)
    : items.filter((x) => x.page && x.slug !== slug).slice(0, 3);
  if (!others.length) return null;
  return (
    <section className="bg-[#F5F0FF] py-20 sm:py-24" data-testid="cs-more">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <h2 className="text-2xl font-extrabold text-[#13052A] sm:text-3xl">More case studies</h2>
        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">{others.map((x, i) => <Reveal key={x.slug} className="h-full"><CaseCard c={x} i={i} /></Reveal>)}</div>
        <Link to="/results" className="btn-primary mt-8" data-testid="cs-all-results">View All Case Studies <ArrowUpRight className="h-4 w-4" /></Link>
      </div>
    </section>
  );
};

const Aside = ({ c }) => (
  <div className="rounded-[28px] border border-white/10 bg-white/5 p-6 backdrop-blur-xl" data-testid="cs-hero-aside">
    <p className="text-[11px] font-bold uppercase tracking-widest text-violet-200">{c.category_line || `${c.client} · ${c.industry}`}</p>
    {c.location && <p className="mt-1 text-xs text-violet-200" data-testid="cs-location">{c.location}{c.campaign_period ? ` · ${c.period_label || "Campaign period"}: ${c.campaign_period}` : ""}</p>}
    <p className="mt-3 text-2xl font-extrabold leading-tight" data-testid="cs-highlight"><span className="bg-gradient-to-r from-[#C4B5FD] to-[#60A5FA] bg-clip-text text-transparent">{c.hero_headline || c.highlight}</span></p>
    {!c.hero_headline && <p className="mt-3 text-sm leading-relaxed text-violet-100">{c.highlight_note}</p>}
    {list(c.facts).length > 0 && (
      <dl className="mt-4 space-y-2 border-t border-white/10 pt-4 text-sm" data-testid="cs-facts">
        {c.facts.map(([k, v]) => <div key={k} className="flex gap-2"><dt className="w-20 shrink-0 font-bold text-violet-200">{k}</dt><dd className="text-violet-50">{v}</dd></div>)}
      </dl>
    )}
  </div>
);

const Body = ({ c }) => {
  if (c.layout === "seo") {
    return (
      <>
        <CampaignMetrics c={c} />
        <SeoChallenge c={c} />
        <SeoStrategy c={c} />
        {list(c.seo_results).map((r, i) => <SeoResult key={r.id} r={r} i={i} />)}
        <SeoEnquiries c={c} />
        <SeoOutcome c={c} />
      </>
    );
  }
  if (c.layout === "campaigns") {
    return (
      <>
        <CampaignMetrics c={c} />
        <CampaignIntro c={c} />
        {list(c.campaigns).map((cp, i) => <CampaignSection key={cp.id} cp={cp} i={i} />)}
        <CampaignStrategy c={c} />
        <CampaignWhy c={c} />
      </>
    );
  }
  if (c.story) {
    return (
      <>
        <StoryMetrics c={c} />
        <StoryChallenge c={c} />
        <StoryApproach c={c} />
        <StoryServices c={c} />
        <StoryOutcome c={c} />
        {c.takeaway && <StoryTakeaway c={c} />}
      </>
    );
  }
  return (<><Background c={c} /><Services c={c} /><ResultsBlock c={c} /></>);
};

const creativesLd = (c) => list(c.campaigns).flatMap((cp) => [cp.spotlight, ...list(cp.creatives)].filter(Boolean))
  .map((img) => ({ "@type": "CreativeWork", name: img.caption || img.alt, description: img.alt, image: `${SITE_URL}${img.src}` }));

export default function CaseStudyPage() {
  const { slug } = useParams();
  const { item: c, loading, error } = useItem("case_studies", slug);
  if (error || (c && !c.page)) return <NotFound />;
  if (loading || !c) return <div className="min-h-screen bg-[#13052A]" />;
  const path = `/case-studies/${c.slug}`;
  const crumbs = [["Home", "/"], [c.story || c.layout ? "Case studies" : "Results", "/results"], [c.story || c.layout ? c.client : c.h1, path]];
  const parts = creativesLd(c);
  return (
    <>
      <Seo raw title={c.meta_title} description={c.meta_description} path={path}
        jsonLd={[{ "@type": "Article", headline: c.h1, description: c.meta_description, url: `${SITE_URL}${path}`, mainEntityOfPage: `${SITE_URL}${path}`,
          ...(c.keywords ? { keywords: c.keywords.join(", ") } : {}), ...(c.created_at ? { datePublished: c.created_at.slice(0, 10) } : {}), ...(parts.length ? { image: parts.map((p) => p.image), hasPart: parts } : {}), author: { "@id": `${SITE_URL}/#freya` },
          publisher: PROVIDER, about: { "@type": "Organization", name: c.client.replace(": Group PT Launch", "") }, inLanguage: "en-GB" }, breadcrumbLd(crumbs)]} />
      <PageHero crumbs={crumbs} eyebrow={c.hero_eyebrow || `${c.card_label} · Case study`} lines={[c.h1]} intro={c.summary} aside={<Aside c={c} />}>
        <Link to={c.cta_to || "/contact?type=call"} className="btn-accent" data-testid="cs-hero-cta">{c.cta_label || "Get in Touch"} <ArrowUpRight className="h-4 w-4" /></Link>
      </PageHero>
      <Body c={c} />
      {c.geo && <Geo />}
      <Links c={c} />
      <More slug={c.slug} related={c.related} />
      {c.final_cta ? <StoryCta c={c} /> : <CtaBand title="Let's Grow Your Business" text="Want results like these? Book a free discovery call or request a free digital audit." />}
    </>
  );
}
